import React, { useCallback, useEffect, useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import type { ArchivedSeasonSummary } from '@shootout-comps/core';
import type { League, LeagueFormat, ArchivedSeason, FixtureStatus } from '@shootout-comps/types';
import { useLang } from '@shootout-comps/i18n';
import DivisionTabs from '../../components/DivisionTabs';

interface SeasonHistoryPageProps {
  leagueId: string;
  divisionId: string;
  onBack: () => void;
  onSwitchToTeams: () => void;
  onSwitchToFixtures: () => void;
  onSwitchToStandings: () => void;
}

const FORMAT_LABEL: Record<LeagueFormat, string> = { handicap: '🔴 Handicap', scratch: '🎯 Scratch' };

// Season history is read-only — archived seasons are frozen snapshots, so this page never
// mutates anything. Mirrors DivisionStandingsPage.tsx's table styling for the standings and
// DivisionFixturesPage.tsx's row styling for the fixture list, since both are just being
// re-rendered against frozen data instead of the live division.
export default function SeasonHistoryPage({ leagueId, divisionId, onBack, onSwitchToTeams, onSwitchToFixtures, onSwitchToStandings }: SeasonHistoryPageProps) {
  const { fetchLeague, fetchSeasons, fetchSeason } = useLeague();
  const { t } = useLang();
  const STATUS_LABEL: Record<FixtureStatus, string> = {
    scheduled: t.fixtureStatusScheduled, lineup_set: t.fixtureStatusLineupSet, in_progress: t.fixtureStatusInProgress, complete: t.complete,
  };
  const [league, setLeague] = useState<League | null>(null);
  const [seasons, setSeasons] = useState<ArchivedSeasonSummary[] | null>(null);
  const [selected, setSelected] = useState<ArchivedSeason | null>(null);
  const [error, setError] = useState('');

  const reload = useCallback(() => {
    fetchLeague(leagueId).then(setLeague).catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
    fetchSeasons(leagueId, divisionId).then(setSeasons).catch(e => setError(e instanceof Error ? e.message : 'Failed to load seasons'));
  }, [fetchLeague, fetchSeasons, leagueId, divisionId]);

  useEffect(reload, [reload]);

  const openSeason = async (seasonId: string) => {
    setError('');
    try {
      const season = await fetchSeason(leagueId, divisionId, seasonId);
      setSelected(season);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load season');
    }
  };

  const division = league?.divisions.find(d => d._id === divisionId);

  if (error && !league) return <div style={{ maxWidth: '640px', margin: '2rem auto', padding: '1rem', color: '#e74c3c', textAlign: 'center' }}>{error}</div>;
  if (!league || !division || !seasons) return <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--chalk-dim)' }}>{t.loadingLabel}</div>;

  const th: React.CSSProperties = { textAlign: 'right', padding: '0.5rem 0.6rem', fontSize: '0.68rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 };
  const td: React.CSSProperties = { textAlign: 'right', padding: '0.55rem 0.6rem', fontSize: '0.85rem' };

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      <button onClick={selected ? () => setSelected(null) : onBack} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: 'var(--chalk-dim)', padding: '0.35rem 0.75rem', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1.25rem' }}>
        {selected ? t.backToAllSeasons : `← ${league.name}`}
      </button>

      <h2 style={{ fontFamily: 'Bebas Neue', fontSize: 'clamp(1.5rem,5vw,2rem)', color: 'var(--gold)', marginBottom: '0.5rem' }}>
        {division.name} <span style={{ fontSize: '0.9rem', color: 'var(--chalk-dim)', fontFamily: 'inherit' }}>{FORMAT_LABEL[division.format]}</span>
      </h2>

      {!selected && (
        <DivisionTabs active="history" onSelect={tab => { if (tab === 'teams') onSwitchToTeams(); else if (tab === 'fixtures') onSwitchToFixtures(); else if (tab === 'standings') onSwitchToStandings(); }} />
      )}

      {error && <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginBottom: '1rem' }}>{error}</p>}

      {!selected ? (
        seasons.length === 0 ? (
          <p style={{ color: 'var(--chalk-dim)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>
            {t.noPastSeasonsYetOwner}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {seasons.map(s => (
              <div
                key={s._id}
                onClick={() => openSeason(s._id)}
                style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.9rem 1.1rem', cursor: 'pointer' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{s.seasonLabel}</div>
                  <div style={{ fontSize: '0.73rem', color: 'var(--chalk-dim)', marginTop: '0.2rem' }}>
                    {t.endedLabel} {new Date(s.endedAt).toLocaleDateString()} · ↑{s.promotionSpots} {t.promotedLabel} / ↓{s.relegationSpots} {t.relegatedLabel}
                  </div>
                </div>
                <span style={{ color: 'var(--chalk-dim)' }}>›</span>
              </div>
            ))}
          </div>
        )
      ) : (
        <>
          <h3 style={{ fontSize: '1.1rem', color: 'var(--chalk)', marginBottom: '0.25rem' }}>{selected.seasonLabel}</h3>
          <p style={{ fontSize: '0.75rem', color: 'var(--chalk-dim)', marginBottom: '1rem' }}>
            {t.endedLabel} {new Date(selected.endedAt).toLocaleDateString()} · ↑{selected.promotionSpots} {t.promotedLabel} / ↓{selected.relegationSpots} {t.relegatedLabel}
          </p>

          <h4 style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 0.6rem', fontWeight: 600 }}>{t.finalStandingsLabel}</h4>
          <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ ...th, textAlign: 'left' }}>#</th>
                  <th style={{ ...th, textAlign: 'left' }}>{t.standingsTeamCol}</th>
                  <th style={th}>P</th>
                  <th style={th}>W</th>
                  <th style={th}>D</th>
                  <th style={th}>L</th>
                  <th style={th}>F</th>
                  <th style={th}>A</th>
                  <th style={th}>+/-</th>
                  <th style={{ ...th, color: 'var(--gold)' }}>Pts</th>
                </tr>
              </thead>
              <tbody>
                {selected.standings.map((s, i) => (
                  <tr key={s.teamId} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <td style={{ ...td, textAlign: 'left', color: 'var(--chalk-dim)' }}>{i + 1}</td>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{s.teamName}</td>
                    <td style={td}>{s.played}</td>
                    <td style={td}>{s.won}</td>
                    <td style={td}>{s.drawn}</td>
                    <td style={td}>{s.lost}</td>
                    <td style={td}>{s.framesFor}</td>
                    <td style={td}>{s.framesAgainst}</td>
                    <td style={{ ...td, color: s.frameDifference > 0 ? '#2ecc71' : s.frameDifference < 0 ? '#e74c3c' : 'var(--chalk-dim)' }}>
                      {s.frameDifference > 0 ? `+${s.frameDifference}` : s.frameDifference}
                    </td>
                    <td style={{ ...td, fontWeight: 700, color: 'var(--gold)' }}>{s.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h4 style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 0.6rem', fontWeight: 600 }}>{t.fixturesLabel} ({selected.fixtures.length})</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {selected.fixtures.map(f => (
              <div key={f._id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '0.75rem 1rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {f.homeTeamName} <span style={{ color: 'var(--chalk-dim)', fontWeight: 400 }}>vs</span> {f.awayTeamName}
                    {f.status === 'complete' && (
                      <span style={{ color: 'var(--gold)', fontWeight: 700, marginLeft: '0.5rem' }}>{f.homeFrameWins}–{f.awayFrameWins}</span>
                    )}
                  </div>
                </div>
                <span style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)' }}>{STATUS_LABEL[f.status]}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
